import * as _ from 'lodash'
import TypeAheadInput, { SuggestionSource, TypeAheadInputOpts, MenuItem } from '../../../generic/TypeAheadInput'
import { UnitOfMeasure } from 'types/dtos'
import { UOMByName } from 'types/UnitOfMeasure'
import { lookupUnit } from 'util/quantifier'
import { sanitize } from 'lib/util/StringUtils'

interface UoMInputOpts extends TypeAheadInputOpts<UnitOfMeasure> {
	unitIndex: UOMByName
	unitList: UnitAliasEntry[]
}

export function permuteSpans(parts: string[]) {
	const results = [[parts.join('/')]]
	for (let i = parts.length - 1; i > 0; i--) {
		const prefix = parts.slice(0, i).join('/')
		for (const suffix of permuteSpans(parts.slice(i))) {
			results.push([prefix, ...suffix])
		}
	}
	return results
}

export function getUnitSpanPermutations(str: string) {
	// naively parse the value into unit parts
	const parts = str.split(/\/+/).map(p => p.trim())
	// for every permutation of sequential compound and individual parts, attempt to look up unit
	return permuteSpans(parts)
}

/**
 * Find an a permutation of unit components that have exact unit match for each component
 */
export function lookupCompoundUnit(permutations: string[][], index: UOMByName): UnitOfMeasure {
	const lookupCache = new Map<string, UnitOfMeasure>()
	const lookup = str => {
		let u = lookupCache.get(str)
		if (u === undefined) {
			u = lookupUnit(str, index)
			lookupCache.set(str, u || null)
		}
		return u
	}
	for (const units of permutations) {
		const uoms = []
		if (units.every((str, i) => uoms[i] = lookup(str))) {
			return {
				// synthetic id?
				Name: { Name: uoms.map(u => u.Name.Name).join('/') }
			}
		}
	}
}

export type Lead = [leading: string, trailing: string]

/**
 * Return permutations of compound parts with unresolved trailing units for suggestion generation
 */
export function findLeads(permutations: string[][], index: UOMByName) {
	const lookupCache = new Map<string, UnitOfMeasure>()
	const lookup = str => {
		let u = lookupCache.get(str)
		if (u === undefined) {
			u = lookupUnit(str, index, true)
			//u = findUnit(str, aliases)
			lookupCache.set(str, u || null)
		}
		return u
	}
	const seen = new Set<string>()
	const leads: Lead[] = []
	for (const units of permutations) {
		// we always look for suggestions for entire compound unit, so doesn't matter if it's a direct match
		if (units.length == 1) continue
		const uoms = []
		for (const unit of units) {
			const uom = lookup(unit)
			if (uom) {
				uoms.push(uom.Name.Name)
			} else {
				break
			}
		}
		if (uoms.length) {
			if (uoms.length == units.length) {
				uoms.pop()
			}
			const leading = uoms.join('/')
			// all entries with the same leading part (equivalent configuration of distinct units) will have the same trailing part
			if (!seen.has(leading)) {
				leads.push([leading, units.slice(uoms.length).join('/')])
				seen.add(leading)
			}
		}
	}
	return leads
}

interface UnitAliasEntry {
	aliases: string[]
	unit: UnitOfMeasure
}

export class UnitsSuggestionSource implements SuggestionSource<UnitOfMeasure> {
	constructor(private index: UOMByName, private unitList: UnitAliasEntry[]) {
	}
	private lookupUnit(value) {
		value = (value || '').trim().replace(/[\/\s]*$/, '') // trim trailing slashes
		if (value.startsWith('/')) return
		const exact = lookupUnit(value, this.index)
		if (exact) return exact
		return lookupCompoundUnit(getUnitSpanPermutations(value), this.index)
	}

	findExact(value: string): Promise<UnitOfMeasure> {
		return Promise.resolve(this.lookupUnit(value))
	}

	private searchUnit(str) {
		const retext = _.escapeRegExp(str)
		const startsWith = new RegExp('^' + retext, 'ig')
		const re = new RegExp(retext, 'i')
		return _.sortBy(this.unitList.reduce((results, {aliases, unit}) => {
			for (const alias of aliases) {
				const name = sanitize(alias)
				if (re.test(name)) {
					results.push([unit, name])
					break
				}
			}
			return results
		}, []), ([unit, name]) => {
			startsWith.lastIndex = 0
			return startsWith.test(name) ? 0 : 1
		}).map(([unit]) => unit)
	}

	findMatches(query: string): Promise<UnitOfMeasure[]> {
		query = (query || '').trim()
		const trimmedQuery = query.replace(/[\/\s]*$/, '') // trim trailing slashes
		const leads = [['', query]].concat(findLeads(getUnitSpanPermutations(trimmedQuery), this.index).concat())
		const suggestions = new Map<string, UnitOfMeasure>()
		const seen = new Set<string>()
		const searchCache = new Map()
		const findMatches = str => {
			let result = searchCache.get(str)
			if (result === undefined) {
				result = this.searchUnit(sanitize(str))
				searchCache.set(str, result || null)
			}
			return result
		}
		const addSuggestions = uoms => {
			for (const uom of uoms) {
				let existing = suggestions.get(uom.Name.Name)
				if (!existing || uom.Id && !existing.Id) {
					existing = uom
					suggestions.set(uom.Name.Name, existing)
				}
			}
		}
		for (const [leading, trailing] of leads) {
			if (leading && trailing) {
				const tails = findMatches(trailing)
				addSuggestions(tails.map(unit => ({
					// synthetic id?
					Name: { Name: leading + '/' + unit.Name.Name }
				})))
			} else {
				const text = leading || trailing
				if (seen.has(text)) continue
				seen.add(text)
				addSuggestions(findMatches(text))
			}
		}
		return Promise.resolve(Array.from(suggestions.values()))
	}
	itemToText(item: UnitOfMeasure) {
		return item.Name?.Name
	}
	itemToEntry(item: UnitOfMeasure) {
		return {
			label: item.Name?.Name
		}
	}
}

export default class UnitOfMeasureInput extends TypeAheadInput.with('.form-control', { placeholder: 'Unit',
	onblur(event) {
			var text = this.content.valueOf().toLowerCase().trim();

			$(this).typeahead('val', text) 

			//needs a slight delay to allow previous line to finish
			setTimeout(() => {
				$(this).typeahead('close')
			}, 200)

	}
}) {
	created(properties: UoMInputOpts) {
		// caller should pass units as property
		properties.source = new UnitsSuggestionSource(properties.unitIndex, properties.unitList)
		super.created(properties)
	}
}